import type { QueryKey } from '@tanstack/react-query';

import useInfiniteQuery from '../../../ReactQuery/useInfiniteQuery';
import { get } from '../../../redux/api';
import { nuc } from '../../../utils';
import type { PaginatedResponseWithTotal } from '../../../../shared/types/routes/pagination';
import type { RevisionScan } from '../../../types';

export type ListRevisionScansAPIResponse = PaginatedResponseWithTotal<RevisionScan>;

/**
 * Query key prefix shared by all revision-scans queries for a given revision,
 * regardless of pageSize. Use this when invalidating across every variant
 * (e.g. after a new scan completes).
 */
export const getRevisionScansQueryKeyPrefix = (revisionLocator: string): QueryKey =>
  ['revision', revisionLocator, 'scans'];

export const getRevisionScansQueryKey = (
  revisionLocator: string,
  pageSize: number,
): QueryKey => [...getRevisionScansQueryKeyPrefix(revisionLocator), pageSize];

function getRevisionScans({ pageParam = 1 }, revisionLocator: string, pageSize: number) {
  return get<ListRevisionScansAPIResponse>(`/api/revisions/${nuc(revisionLocator)}/scans`, false, {
    pageSize,
    page: pageParam,
  });
}

/**
 * Helper hook for fetching the scans of a user's revision.
 * @param revisionLocator - The revision locator of the user's revision.
 * @param pageSize - The number of matches to fetch per page.
 * @param shouldFetch - Whether to fetch the matches or not. Used to prevent fetching until matches are needed to be displayed.
 * @returns An infinite query for the matches of the given component.
 */
export default function useRevisionScans(
  revisionLocator: string,
  pageSize: number,
  shouldFetch: boolean = true,
) {
  return useInfiniteQuery<ListRevisionScansAPIResponse>({
    queryKey: getRevisionScansQueryKey(revisionLocator, pageSize),
    queryFn: (context) => getRevisionScans(context, revisionLocator, pageSize),
    enabled: shouldFetch,
  });
}
